/* =========================================================================
   USMA — moteur d'interaction.

   Portage du bundle React de la maquette. Correspondances :

     Lenis + gsap.ticker      -> identique, meme easing, meme duration
     GSAP ScrollTrigger       -> identique
     Framer Motion            -> remplace par GSAP (Framer est React-only)
     useInView                -> remplace par ScrollTrigger.batch
     etat React (activeIndex) -> une variable + une garde, comme le ref check
                                 recommande dans la spec de perf

   Le fichier ne fait rien tant que GSAP n'a pas repondu : si le CDN tombe,
   on marque la page .usma-no-js et le CSS rend tout visible. Le panier et
   le selecteur de variantes, eux, ne dependent pas de GSAP et tournent dans
   tous les cas — une animation qui saute est un detail, un bouton
   « Ajouter au panier » mort est une vente perdue.
   ========================================================================= */
(function () {
  'use strict';

  if (window.__usmaBooted) return;
  window.__usmaBooted = true;

  var root = document.documentElement;
  var hasGsap = typeof window.gsap !== 'undefined' &&
                typeof window.ScrollTrigger !== 'undefined';
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var animate = hasGsap && !reduced;

  root.classList.add(animate ? 'usma-ready' : 'usma-no-js');

  var $  = function (s, c) { return (c || document).querySelector(s); };
  var $$ = function (s, c) {
    return Array.prototype.slice.call((c || document).querySelectorAll(s));
  };

  if (animate) window.gsap.registerPlugin(window.ScrollTrigger);

  /* ------------------------------------------------------------ argent -- */

  var moneyFormat = window.usmaMoneyFormat || '{{amount}} €';

  function money(cents) {
    var n = (cents || 0) / 100;
    function withDelims(val, sep, dec, places) {
      var fixed = val.toFixed(places === 0 ? 0 : 2);
      var parts = fixed.split('.');
      var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, sep);
      return places === 0 ? whole : whole + dec + parts[1];
    }
    return moneyFormat.replace(
      /\{\{\s*(\w+)\s*\}\}/,
      function (_, name) {
        switch (name) {
          case 'amount':                       return withDelims(n, ',', '.', 2);
          case 'amount_no_decimals':           return withDelims(n, ',', '.', 0);
          case 'amount_with_comma_separator':  return withDelims(n, '.', ',', 2);
          case 'amount_no_decimals_with_comma_separator':
                                               return withDelims(n, '.', ',', 0);
          case 'amount_with_space_separator':  return withDelims(n, ' ', ',', 2);
          case 'amount_no_decimals_with_space_separator':
                                               return withDelims(n, ' ', ',', 0);
          default:                             return withDelims(n, ' ', ',', 2);
        }
      }
    );
  }

  /* ----------------------------------------------------- scroll fluide -- */

  var lenis = null;

  function initLenis() {
    if (!animate || typeof window.Lenis === 'undefined') return;

    lenis = new window.Lenis({
      duration: 1.2,
      easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
      smoothWheel: true
    });

    lenis.on('scroll', window.ScrollTrigger.update);
    window.gsap.ticker.add(function (time) { lenis.raf(time * 1000); });
    window.gsap.ticker.lagSmoothing(0);
  }

  function stopScroll(yes) {
    if (lenis) { yes ? lenis.stop() : lenis.start(); }
    document.body.style.overflow = yes ? 'hidden' : '';
  }

  /* -------------------------------------------------------- navigation -- */

  function initNav() {
    var nav = $('[data-usma-nav]');
    if (!nav) return;

    var stuck = false;
    var onScroll = function () {
      var y = window.scrollY || window.pageYOffset;
      var next = y > 50;
      if (next !== stuck) { stuck = next; nav.classList.toggle('is-stuck', next); }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    var drawer = $('[data-usma-menu]');
    var open   = $('[data-usma-menu-open]');
    var close  = $('[data-usma-menu-close]');

    if (drawer && open) {
      var toggle = function (yes) {
        drawer.classList.toggle('is-open', yes);
        open.setAttribute('aria-expanded', String(yes));
        stopScroll(yes);
      };
      open.addEventListener('click', function () { toggle(true); });
      if (close) close.addEventListener('click', function () { toggle(false); });
      $$('a', drawer).forEach(function (a) {
        a.addEventListener('click', function () { toggle(false); });
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') toggle(false);
      });
    }
  }

  /* ------------------------------------------------------ reveals texte -- */

  function initReveals() {
    if (!animate) return;

    window.ScrollTrigger.batch('.usma-rise', {
      start: 'top 85%',
      onEnter: function (els) {
        window.gsap.to(els, {
          y: 0, opacity: 1, duration: 1, ease: 'power3.out',
          stagger: .09, overwrite: true
        });
      }
    });

    window.ScrollTrigger.batch('.usma-mask', {
      start: 'top 82%',
      onEnter: function (els) {
        window.gsap.to(els, {
          clipPath: 'inset(0 0 0% 0)', duration: 1.25,
          ease: 'power3.inOut', stagger: .12, overwrite: true
        });
      }
    });
  }

  /* --------------------------------------------------------------- hero -- */

  function initHero() {
    var hero = $('[data-usma-hero]');
    if (!hero || !animate) return;

    var inner = $('.usma-hero__inner', hero);

    window.gsap.from($$('.usma-hero__inner > *', hero), {
      y: 100, opacity: 0, duration: 1.15, ease: 'power3.out', stagger: .13,
      delay: .15
    });

    window.gsap.to(inner, {
      scale: .85, opacity: 0, ease: 'none',
      scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true }
    });

    var media = $('.usma-hero__media', hero);
    if (media) {
      window.gsap.to(media, {
        yPercent: 14, ease: 'none',
        scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true }
      });
    }
  }

  /* ------------------------------------------------------------ piliers -- */

  function initPiliers() {
    var sec = $('[data-usma-piliers]');
    if (!sec) return;

    var panels = $$('[data-usma-pilier]', sec);
    var dots   = $$('.usma-piliers__dot', sec);
    var bg     = $('.usma-piliers__bg', sec);
    if (!panels.length) return;

    var index = -1;

    function show(i, instant) {
      // La garde : sans elle, onUpdate rejouerait l'animation a chaque
      // pixel de scroll — c'est le ref check de la spec React.
      if (i === index) return;
      index = i;

      panels.forEach(function (p, n) {
        var on = n === i;
        p.hidden = !on;
        if (on && animate && !instant) {
          window.gsap.fromTo($$('[data-usma-pilier-in]', p),
            { y: 60, opacity: 0 },
            { y: 0, opacity: 1, duration: .8, ease: 'power3.out', stagger: .07 });
          var img = $('img', p);
          if (img) {
            window.gsap.fromTo(img,
              { yPercent: 100, opacity: 0 },
              { yPercent: 0, opacity: 1, duration: 1, ease: 'power3.out' });
          }
        }
      });

      dots.forEach(function (d, n) { d.classList.toggle('is-on', n === i); });

      if (bg) {
        var tint = panels[i].getAttribute('data-usma-tint');
        if (tint) bg.style.backgroundColor = tint;
      }
    }

    show(0, true);

    dots.forEach(function (d, n) {
      d.addEventListener('click', function () { show(n); });
    });

    // Un seul pilier : les pastilles ne servent plus a rien, et pinner la
    // section creerait 300 px de scroll mort ou il ne se passe rien.
    if (panels.length < 2) {
      // display:none en dur : l'attribut hidden ne fait pas le poids face au
      // display:flex du conteneur.
      var boite = $('.usma-piliers__dots', sec);
      if (boite) boite.style.display = 'none';
      return;
    }

    if (!animate) {
      // Sans GSAP, le scroll pinne n'existe pas : on garde les pastilles
      // comme unique moyen de navigation, et tout reste atteignable.
      return;
    }

    // Un seul panneau : il n'y a rien a faire defiler d'un etat a l'autre.
    // Epingler la section immobiliserait la page plusieurs centaines de
    // pixels pour ne rien montrer de nouveau — le visiteur croit que le
    // scroll a lache. On laisse la section defiler normalement.
    if (panels.length < 2) return;

    window.ScrollTrigger.create({
      trigger: sec,
      start: 'top top',
      end: '+=' + (300 * panels.length),
      pin: true,
      scrub: true,
      onUpdate: function (self) {
        var i = Math.min(panels.length - 1,
                         Math.floor(self.progress * panels.length));
        show(i);
      }
    });
  }

  /* ------------------------------------------------------------ origine -- */

  function initOrigine() {
    var sec = $('[data-usma-origine]');
    if (!sec || !animate) return;

    var img = $('.usma-origine__media img', sec);
    if (img) {
      window.gsap.fromTo(img, { yPercent: -10 }, {
        yPercent: 10, ease: 'none',
        scrollTrigger: { trigger: sec, start: 'top bottom', end: 'bottom top', scrub: true }
      });
    }
  }

  /* -------------------------------------------------------------- macro -- */

  function initMacro() {
    var sec = $('[data-usma-macro]');
    if (!sec || !animate) return;

    var bg   = $('.usma-macro__bg', sec);
    var text = $('.usma-macro__text', sec);

    if (bg) {
      window.gsap.fromTo(bg, { scale: 1, yPercent: -8 },
        { scale: 1.25, yPercent: 8, ease: 'none',
          scrollTrigger: { trigger: sec, start: 'top bottom', end: 'bottom top', scrub: true } });
    }
    if (text) {
      window.gsap.fromTo(text, { opacity: 0, y: 40 },
        { opacity: 1, y: 0,
          scrollTrigger: { trigger: sec, start: 'top 60%', end: 'top 20%', scrub: true } });
      window.gsap.to(text, {
        opacity: 0,
        scrollTrigger: { trigger: sec, start: 'bottom 80%', end: 'bottom 40%', scrub: true }
      });
    }
  }

  /* ------------------------------------------------------- avant/apres -- */

  function initCompare() {
    $$('[data-usma-compare]').forEach(function (box) {
      var before = $('.usma-ab__before', box);
      var handle = $('.usma-ab__handle', box);
      if (!before || !handle) return;

      var dragging = false;

      function setAt(clientX) {
        var r = box.getBoundingClientRect();
        var pct = ((clientX - r.left) / r.width) * 100;
        pct = Math.max(0, Math.min(100, pct));
        before.style.clipPath = 'inset(0 ' + (100 - pct) + '% 0 0)';
        handle.style.left = pct + '%';
        box.setAttribute('aria-valuenow', Math.round(pct));
      }

      box.addEventListener('pointerdown', function (e) {
        dragging = true; box.setPointerCapture(e.pointerId); setAt(e.clientX);
      });
      box.addEventListener('pointermove', function (e) {
        if (dragging) setAt(e.clientX);
      });
      ['pointerup', 'pointercancel'].forEach(function (ev) {
        box.addEventListener(ev, function () { dragging = false; });
      });

      // Clavier : le comparateur est un slider, il doit l'etre pour tout
      // le monde, pas seulement a la souris.
      box.setAttribute('role', 'slider');
      box.setAttribute('tabindex', '0');
      box.setAttribute('aria-valuemin', '0');
      box.setAttribute('aria-valuemax', '100');
      box.addEventListener('keydown', function (e) {
        var cur = parseFloat(box.getAttribute('aria-valuenow') || '50');
        if (e.key === 'ArrowLeft')  { cur -= 4; }
        else if (e.key === 'ArrowRight') { cur += 4; }
        else return;
        e.preventDefault();
        cur = Math.max(0, Math.min(100, cur));
        before.style.clipPath = 'inset(0 ' + (100 - cur) + '% 0 0)';
        handle.style.left = cur + '%';
        box.setAttribute('aria-valuenow', Math.round(cur));
      });
    });
  }

  function initRail() {
    var wrap = $('[data-usma-rail-wrap]');
    var rail = $('[data-usma-rail]');
    if (!wrap || !rail || !animate) return;

    var items = $$('.usma-ab__item', rail);
    if (items.length < 2) return;

    var distance = function () { return rail.scrollWidth - window.innerWidth; };
    if (distance() <= 0) return;

    window.gsap.to(rail, {
      x: function () { return -distance(); },
      ease: 'none',
      scrollTrigger: {
        trigger: wrap,
        start: 'top top',
        end: function () { return '+=' + distance(); },
        pin: true,
        scrub: 1,
        invalidateOnRefresh: true
      }
    });

    // Skew a la velocite : quickSetter evite un style recalcule par frame.
    var setSkew = window.gsap.quickSetter(items, 'skewX', 'deg');
    var clamp = window.gsap.utils.clamp(-15, 15);
    var proxy = { skew: 0 };

    window.ScrollTrigger.create({
      onUpdate: function (self) {
        var v = clamp(self.getVelocity() / -260);
        if (Math.abs(v) > Math.abs(proxy.skew)) {
          proxy.skew = v;
          window.gsap.to(proxy, {
            skew: 0, duration: .7, ease: 'power3',
            overwrite: true,
            onUpdate: function () { setSkew(proxy.skew); }
          });
        }
      }
    });
  }

  function initModal() {
    var modal = $('[data-usma-modal]');
    if (!modal) return;

    var img   = $('img', modal);
    var cap   = $('p', modal);
    var items = $$('[data-usma-modal-open]');
    var at    = 0;

    function open(i) {
      at = (i + items.length) % items.length;
      var src = items[at].getAttribute('data-usma-full') ||
                $('img', items[at]).src;
      img.src = src;
      img.alt = items[at].getAttribute('data-usma-alt') || '';
      if (cap) cap.textContent = items[at].getAttribute('data-usma-caption') || '';
      modal.classList.add('is-open');
      stopScroll(true);
    }
    function close() { modal.classList.remove('is-open'); stopScroll(false); }

    items.forEach(function (it, i) {
      it.addEventListener('click', function () { open(i); });
    });
    var prev = $('[data-usma-modal-prev]', modal);
    var next = $('[data-usma-modal-next]', modal);
    if (prev) prev.addEventListener('click', function () { open(at - 1); });
    if (next) next.addEventListener('click', function () { open(at + 1); });
    $$('[data-usma-modal-close]', modal).forEach(function (b) {
      b.addEventListener('click', close);
    });
    modal.addEventListener('click', function (e) { if (e.target === modal) close(); });
    document.addEventListener('keydown', function (e) {
      if (!modal.classList.contains('is-open')) return;
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft') open(at - 1);
      if (e.key === 'ArrowRight') open(at + 1);
    });
  }

  /* ------------------------------------------------------------- panier -- */

  var cartEl = null;

  function cartOpen(yes) {
    if (!cartEl) cartEl = $('[data-usma-cart]');
    if (!cartEl) return;
    cartEl.classList.toggle('is-open', yes);
    stopScroll(yes);
  }

  function renderCart(cart) {
    var body  = $('[data-usma-cart-body]');
    var total = $('[data-usma-cart-total]');
    var count = $$('[data-usma-cart-count]');

    count.forEach(function (c) {
      c.textContent = cart.item_count;
      c.hidden = cart.item_count === 0;
    });

    if (total) total.textContent = money(cart.total_price);
    if (!body) return;

    if (!cart.items.length) {
      body.innerHTML = '<p class="usma-cart__empty">Votre panier est vide.</p>';
      return;
    }

    body.innerHTML = cart.items.map(function (it) {
      var img = it.image
        ? '<img src="' + it.image.replace(/(\.[a-z]+)(\?|$)/i, '_140x$1$2') +
          '" alt="" loading="lazy">'
        : '<img alt="">';
      var variant = (it.variant_title && it.variant_title !== 'Default Title')
        ? '<small>' + it.variant_title + '</small>' : '';
      return '<div class="usma-cart__line">' + img +
        '<div style="flex:1">' +
          '<h3>' + it.product_title + '</h3>' + variant +
          '<div style="display:flex;align-items:center;justify-content:space-between;margin-top:8px">' +
            '<span class="usma-qty" style="margin:0">' +
              '<button type="button" data-usma-line="' + it.key + '" data-usma-to="' + (it.quantity - 1) + '" aria-label="Retirer un">&minus;</button>' +
              '<input value="' + it.quantity + '" readonly tabindex="-1" aria-label="Quantité">' +
              '<button type="button" data-usma-line="' + it.key + '" data-usma-to="' + (it.quantity + 1) + '" aria-label="Ajouter un">+</button>' +
            '</span>' +
            '<b>' + money(it.line_price) + '</b>' +
          '</div>' +
        '</div></div>';
    }).join('');
  }

  // Ne rejette jamais. Deux raisons :
  //   . hors Shopify (maquette de controle, page servie a plat), /cart.js
  //     renvoie du HTML et r.json() leve — inutile de polluer la console ;
  //   . apres un ajout reussi, un echec de rafraichissement ferait tomber
  //     la chaine dans le catch du formulaire, qui annoncerait « l'ajout a
  //     echoue » alors que l'article est bien au panier. Mentir au client
  //     sur l'etat de son panier est pire que ne pas ouvrir le tiroir.
  function refreshCart(open) {
    return fetch(window.Shopify && window.Shopify.routes
        ? window.Shopify.routes.root + 'cart.js' : '/cart.js', {
      headers: { Accept: 'application/json' }
    })
      .then(function (r) {
        if (!r.ok) throw new Error('cart.js ' + r.status);
        return r.json();
      })
      .then(function (cart) { renderCart(cart); return cart; })
      .catch(function () { return null; })
      .then(function (cart) { if (open) cartOpen(true); return cart; });
  }

  function changeLine(key, qty) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: Math.max(0, qty) })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('change.js ' + r.status);
        return r.json();
      })
      .then(renderCart)
      .catch(function () {
        // Si la modification n'a pas abouti, le tiroir afficherait la
        // quantite demandee alors que le panier reel n'a pas bouge. On
        // resynchronise sur l'etat serveur plutot que de laisser un
        // chiffre faux sous les yeux du client.
        return refreshCart(false);
      });
  }

  function initCart() {
    cartEl = $('[data-usma-cart]');

    $$('[data-usma-cart-open]').forEach(function (b) {
      b.addEventListener('click', function (e) {
        e.preventDefault(); refreshCart(true);
      });
    });
    $$('[data-usma-cart-close]').forEach(function (b) {
      b.addEventListener('click', function () { cartOpen(false); });
    });
    if (cartEl) {
      var veil = $('.usma-cart__veil', cartEl);
      if (veil) veil.addEventListener('click', function () { cartOpen(false); });
      cartEl.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-usma-line]');
        if (!btn) return;
        changeLine(btn.getAttribute('data-usma-line'),
                   parseInt(btn.getAttribute('data-usma-to'), 10));
      });
    }
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') cartOpen(false);
    });

    refreshCart(false);
  }

  /* ------------------------------------------------------------ produit -- */

  function initProduct() {
    // Le formulaire vient de {% form 'product' %} : on le cible par sa
    // classe, le tag Liquid n'acceptant pas d'attribut data- en argument.
    var form = $('form.usma-form');
    if (!form) return;

    var data = $('[data-usma-variants]');
    var variants = data ? JSON.parse(data.textContent) : [];
    var idField  = $('[data-usma-variant-id]', form);
    var priceIns = $('[data-usma-price]');
    var priceDel = $('[data-usma-price-compare]');
    var submit   = $('[data-usma-submit]', form);
    var buyNow   = $('[data-usma-buynow]', form);
    var stock    = $('[data-usma-stock]');
    var mainImg  = $('[data-usma-main-img]');
    var unitaire = $('[data-usma-unitaire]');

    // Nombre d'unites d'une variante, lu en tete de son nom : « 3 pains »
    // donne 3, « 1 pain » donne 1, tout le reste donne 1.
    function unites(v) {
      var m = (v.options[0] || '').match(/^\s*(\d+)/);
      return m ? parseInt(m[1], 10) : 1;
    }

    // Prix de reference a l'unite : celui de la plus petite quantite
    // proposee. C'est lui qui sert de base au calcul de l'economie, et il
    // vient de Shopify — pas d'un prix barre saisi a la main.
    var refUnitaire = (function () {
      var best = null;
      variants.forEach(function (v) {
        var n = unites(v);
        if (best === null || n < best.n) best = { n: n, prix: v.price / n };
      });
      return best ? best.prix : null;
    })();

    function selected() {
      return $$('[data-usma-option]', form)
        .filter(function (i) { return i.checked; })
        .map(function (i) { return i.value; });
    }

    function match(opts) {
      return variants.filter(function (v) {
        return v.options.every(function (o, i) { return o === opts[i]; });
      })[0];
    }

    function apply() {
      var v = match(selected());

      if (!v) {
        // Aucune correspondance — deux situations tres differentes :
        //
        //   . la combinaison choisie n'existe pas : c'est un vrai
        //     indisponible, on coupe l'achat ;
        //   . il n'y a aucune donnee de variante (balise JSON absente ou
        //     illisible) : on ne sait rien. Couper l'achat dans ce cas
        //     rendrait le produit invendable alors que Liquid a deja rendu
        //     l'etat correct cote serveur. On ne touche a rien.
        if (!variants.length) return;

        if (submit) { submit.disabled = true; submit.textContent = 'Indisponible'; }
        if (buyNow) buyNow.hidden = true;
        return;
      }

      if (idField) idField.value = v.id;

      if (priceIns) priceIns.textContent = money(v.price);
      if (priceDel) {
        var promo = v.compare_at_price && v.compare_at_price > v.price;
        priceDel.hidden = !promo;
        if (promo) priceDel.textContent = money(v.compare_at_price);
      }

      if (submit) {
        submit.disabled = !v.available;
        submit.textContent = v.available ? 'Ajouter au panier' : 'Rupture de stock';
      }
      if (buyNow) buyNow.hidden = !v.available;

      if (stock) {
        stock.classList.toggle('is-out', !v.available);
        var label = $('span', stock);
        if (label) {
          label.textContent = v.available
            ? 'En stock'
            : 'Momentanément indisponible';
        }
      }

      if (mainImg && v.featured_image && v.featured_image.src) {
        mainImg.src = v.featured_image.src;
      }

      // Lot : on affiche le prix a l'unite et l'economie reelle. Aucune
      // unite n'est presentee comme « offerte » — le client paie les trois.
      if (unitaire) {
        var n = unites(v);
        if (n > 1 && refUnitaire) {
          var plein = Math.round(refUnitaire * n);
          var gain = plein - v.price;
          var pct = Math.round((gain / plein) * 100);
          unitaire.hidden = false;
          unitaire.innerHTML = 'Soit <b>' + money(Math.round(v.price / n)) +
            ' l\'unité</b> — ' + money(gain) + ' d\'économie (' + pct + ' %)';
        } else {
          unitaire.hidden = true;
          unitaire.textContent = '';
        }
      }

      // On garde l'URL alignee sur la variante : un lien copie-colle depuis
      // la barre d'adresse doit rouvrir la meme selection.
      if (window.history.replaceState) {
        var u = new URL(window.location.href);
        u.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', u.toString());
      }
    }

    $$('[data-usma-option]', form).forEach(function (i) {
      i.addEventListener('change', apply);
    });
    apply();

    // Quantite
    $$('[data-usma-qty]').forEach(function (box) {
      var input = $('input', box);
      $$('button', box).forEach(function (b) {
        b.addEventListener('click', function () {
          var step = parseInt(b.getAttribute('data-usma-step'), 10);
          var next = Math.max(1, (parseInt(input.value, 10) || 1) + step);
          input.value = next;
        });
      });
    });

    // Ajout au panier sans quitter la page
    form.addEventListener('submit', function (e) {
      // Les boutons de paiement accelere soumettent ce meme formulaire et
      // sont pilotes par le script de Shopify. Les intercepter enverrait
      // l'article au panier sans jamais ouvrir le checkout : on ne prend
      // la main que si c'est bien notre bouton qui a declenche l'envoi.
      if (e.submitter && e.submitter !== submit) return;

      e.preventDefault();
      if (!submit || submit.disabled) return;

      var label = submit.textContent;
      submit.disabled = true;
      submit.textContent = 'Ajout…';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      })
        .then(function (r) {
          if (!r.ok) return r.json().then(function (err) { throw err; });
          return r.json();
        })
        .then(function () { return refreshCart(true); })
        .catch(function (err) {
          // Shopify renvoie ici les vrais refus (stock insuffisant,
          // variante desactivee). Les taire ferait croire a un ajout.
          window.alert(err && err.description
            ? err.description
            : "L'ajout au panier a échoué. Réessayez.");
        })
        .then(function () {
          submit.disabled = false;
          submit.textContent = label;
        });
    });

    // Galerie : vignettes
    $$('[data-usma-thumb]').forEach(function (t) {
      t.addEventListener('click', function () {
        if (mainImg) mainImg.src = t.getAttribute('data-usma-src');
        $$('[data-usma-thumb]').forEach(function (o) {
          o.classList.toggle('is-on', o === t);
        });
      });
    });
  }

  /* --------------------------------------------------- barre d'annonce -- */

  /* Un vrai compte a rebours : il vise une date de fin fixe, la meme pour
     tout le monde, et ne repart pas a zero d'une visite a l'autre. Une fois
     l'echeance passee, l'element porteur de l'offre disparait au lieu
     d'afficher un decompte negatif ou de recommencer. */
  function initAnnonce() {
    // L'en-tete est en position fixe : il doit connaitre la hauteur de la
    // barre pour se caler dessous plutot que de la recouvrir.
    var barre = $('.usma-annonce');
    if (barre) {
      var poser = function () {
        document.documentElement.style.setProperty(
          '--usma-annonce-h', barre.offsetHeight + 'px');
      };
      poser();
      window.addEventListener('resize', poser, { passive: true });
    }

    var compteurs = $$('[data-usma-compte]');
    if (!compteurs.length) return;

    // Deux modes. Une date fixe : meme echeance pour tout le monde. Ou une
    // duree en minutes : l'echeance est calculee a la premiere visite puis
    // gardee en localStorage, de sorte qu'un rechargement ne la repousse
    // pas. Une fois passee, elle ne repart pas.
    var fin = Date.parse(compteurs[0].getAttribute('data-usma-fin'));

    if (isNaN(fin)) {
      var minutes = parseInt(compteurs[0].getAttribute('data-usma-duree'), 10);
      if (minutes > 0) {
        var cle = 'usma-fin-offre';
        var garde = null;
        try { garde = window.localStorage.getItem(cle); } catch (e) {}
        fin = garde ? parseInt(garde, 10) : NaN;
        if (isNaN(fin)) {
          fin = Date.now() + minutes * 60000;
          try { window.localStorage.setItem(cle, String(fin)); } catch (e) {}
        }
      }
    }

    if (isNaN(fin)) {
      $$('[data-usma-offre]').forEach(function (e) { e.remove(); });
      return;
    }

    function deux(n) { return n < 10 ? '0' + n : String(n); }

    function battement() {
      var reste = fin - Date.now();
      if (reste <= 0) {
        $$('[data-usma-offre]').forEach(function (e) { e.remove(); });
        clearInterval(minuteur);
        return;
      }
      var s = Math.floor(reste / 1000);
      var j = Math.floor(s / 86400);
      var h = Math.floor((s % 86400) / 3600);
      var m = Math.floor((s % 3600) / 60);
      var sec = s % 60;
      // Les secondes sont toujours affichees : sans elles le compteur parait
      // fige, et un decompte qui ne bouge pas ne decompte rien aux yeux du
      // visiteur.
      var texte = (j > 0 ? j + ' j ' : '')
                + deux(h) + ' h ' + deux(m) + ' min ' + deux(sec) + ' s';
      compteurs.forEach(function (c) { c.textContent = texte; });
    }

    battement();
    var minuteur = setInterval(battement, 1000);
  }

  /* --------------------------------------------------------------- boot -- */

  function boot() {
    initAnnonce();
    initLenis();
    initNav();
    initReveals();
    initHero();
    initPiliers();
    initOrigine();
    initMacro();
    initCompare();
    initRail();
    initModal();
    initCart();
    initProduct();

    if (animate) {
      window.addEventListener('load', function () { window.ScrollTrigger.refresh(); });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
